import { apiClient } from '@/config/axios';

import type {
  AnswerResponse,
  AssessmentConfig,
  AssessmentProgressResponse,
  AssessmentStaticResponse,
  BackendQuestion,
  Option,
  Question,
  QuestionType,
  StartResponse,
} from '../types';

const BASE = '/api/v2/assessment';

// ─── Mappers ─────────────────────────────────────────────────────────────────

/**
 * Backend now sends `answers` as `string[]` (plain text).
 * Convert to the frontend Option shape with id = string(index).
 */
function mapOptions(answers: BackendQuestion['answers']): Option[] {
  return (answers ?? []).map((text, index) => ({
    id: String(index),
    text: typeof text === 'string' ? text : String(text),
    order: index,
  }));
}

function mapQuestion(q: BackendQuestion, index: number): Question {
  const base = {
    id: q.question,
    sectionId: 'section-1',
    text: q.name,
    order: index,
    required: true,
    points: 0,
    context: q.context ?? null,
    difficulty: q.difficulty ?? null,
  };

  switch (q.type) {
    case 'radio':
      return { ...base, type: 'radio', options: mapOptions(q.answers) };
    case 'checkbox':
      return { ...base, type: 'checkbox', options: mapOptions(q.answers) };
    case 'text':
      // limit = max characters (excluding HTML tags)
      return { ...base, type: 'text', maxLength: q.limit ?? undefined };
    case 'code':
      // Backend no longer sends `limit` for code — only `starterCode`
      return {
        ...base,
        type: 'code',
        languages: [],
        starterCode: q.starterCode ?? undefined,
      };
    case 'file':
      // limit = max upload size in KB → convert to bytes
      return {
        ...base,
        type: 'file',
        accept: [],
        maxSizeBytes: (q.limit ?? 10240) * 1024,
        limit: q.limit ?? undefined,
      };
    case 'video':
      // limit = max recording duration in seconds
      return {
        ...base,
        type: 'video',
        maxDurationSeconds: q.limit ?? null,
        allowUpload: true,
        allowRecord: true,
      };
    default:
      return { ...base, type: 'text' as const };
  }
}

export function mapStaticResponseToConfig(data: AssessmentStaticResponse): AssessmentConfig {
  const { test, job } = data;
  const questions: Question[] = test.questions.map(mapQuestion);

  return {
    id: String(test.id),
    title: job.name,
    estimatedDuration: Math.ceil(test.allowedTime / 60),
    totalQuestions: test.questionCount,
    totalSections: 1,
    timeLimit: test.allowedTime,
    enableWebcamScreenshots: test.webcamScreenshots,
    disableCopyPaste: test.disableCopyPaste,
    enableBrowserFocus: test.monitorTabs,
    lockTestAfterRefresh: test.lockTestAfterRefresh,
    enableFeedback: test.enableFeedback,
    requireCamera: test.webcamScreenshots,
    allowExtraTime: test.enableExtraTime,
    sections: [
      {
        id: 'section-1',
        title: job.name,
        order: 0,
        questions,
      },
    ],
  };
}

// ─── Answer payload builder ───────────────────────────────────────────────────

/**
 * Converts the frontend value format to what the backend expects:
 * - radio:    value = "2"       → answer = [2]
 * - checkbox: value = ["1","2"] → answer = [1, 2]
 * - text/code: value = "text"  → answer = ["text"]
 * - null/skip                  → answer = []
 */
function buildJsonAnswer(
  type: QuestionType,
  value: string | string[] | null,
): { answer: number[] | string[] } {
  if (value === null) return { answer: [] };

  switch (type) {
    case 'radio':
      return { answer: [parseInt(value as string, 10)] };
    case 'checkbox':
      return { answer: (value as string[]).map((v) => parseInt(v, 10)) };
    case 'text':
    case 'code':
      return { answer: typeof value === 'string' ? [value] : value };
    default:
      return { answer: [] };
  }
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function fetchAssessmentStatic(publicUrl: string): Promise<AssessmentStaticResponse> {
  const response = await apiClient.get<AssessmentStaticResponse>(
    `${BASE}/${publicUrl}/details/static`,
  );
  return response.data;
}

export async function fetchAssessmentProgress(
  publicUrl: string,
): Promise<AssessmentProgressResponse> {
  const response = await apiClient.get<AssessmentProgressResponse>(
    `${BASE}/${publicUrl}/details/progress`,
  );
  return response.data;
}

export async function startAssessment(
  publicUrl: string,
  hasHandicap: boolean,
): Promise<StartResponse> {
  const response = await apiClient.patch<StartResponse>(`${BASE}/${publicUrl}/start`, {
    hasHandicap,
  });
  return response.data;
}

export async function submitAnswer(
  publicUrl: string,
  questionId: string,
  type: QuestionType,
  value: string | string[] | null,
  file?: File,
  isAutoSave = false,
): Promise<AnswerResponse> {
  const url = `${BASE}/${publicUrl}/questions/${questionId}/answer`;

  if (file && (type === 'file' || type === 'video')) {
    const form = new FormData();
    form.append('answer', file);
    // Do NOT set Content-Type manually — axios auto-sets multipart/form-data with the correct boundary
    const response = await apiClient.patch<AnswerResponse>(url, form);
    return response.data;
  }

  const payload = { ...buildJsonAnswer(type, value), isAutoSave };
  const response = await apiClient.patch<AnswerResponse>(url, payload);
  return response.data;
}

export async function lockAssessment(publicUrl: string, reason: string): Promise<void> {
  await apiClient.patch(`${BASE}/${publicUrl}/lock`, { reason });
}
